/**
 * Stellungnahmen-Filter für die Fallübersicht.
 *
 * Die Stellungnahme durchläuft eine kleine eigene Status-Kette, die unabhängig
 * vom generischen `akten.status` läuft (siehe Issue #243):
 *   - offen        — Stellungnahme steht an
 *   - versendet    — Schriftsatz ist raus, wartet auf Gericht/Gegenseite
 *   - abgerechnet  — zugehörige Rechnung wurde bezahlt
 *
 * Wenn dieser Filter aktiv ist, sortiert die Fallübersicht automatisch nach
 * der Schriftsatzfrist des Gerichts (`schriftsatzfrist`, aufsteigend) damit
 * der ablaufende Schriftsatz zuerst sichtbar ist.
 */

export type StellungnahmeStatus = 'offen' | 'versendet' | 'abgerechnet';

export interface StellungnahmeFilterOption {
  value: 'alle' | StellungnahmeStatus;
  label: string;
}

export const STELLUNGNAHME_STATUS_FILTER_OPTIONS: readonly StellungnahmeFilterOption[] = [
  { value: 'alle', label: 'Alle' },
  { value: 'offen', label: 'Offen' },
  { value: 'versendet', label: 'Versendet' },
  { value: 'abgerechnet', label: 'Abgerechnet' },
] as const;

const STATUS_WERTE: readonly StellungnahmeStatus[] = ['offen', 'versendet', 'abgerechnet'];

export function istStellungnahmeStatusFilterAktiv(
  value: string | null | undefined,
): boolean {
  return !!value && value !== 'alle' && STATUS_WERTE.includes(value as StellungnahmeStatus);
}

export function normalisiereStellungnahmeFilter(
  value: string | null | undefined,
): StellungnahmeStatus | null {
  if (!istStellungnahmeStatusFilterAktiv(value)) return null;
  return value as StellungnahmeStatus;
}

export interface AktenSortierung {
  column: string;
  ascending: boolean;
  nullsFirst: boolean;
}

/**
 * Entscheidet über die Default-Sortierung der Fallübersicht.
 * Wenn ein Stellungnahme-Unter-Status gefiltert wird (über den alten
 * Einzel-Select oder den neuen Sub-Status-Filter), wird nach
 * `schriftsatzfrist` aufsteigend sortiert; sonst fällt es auf
 * `defaultSort` zurück (z.B. per-Betrieb-Default aus getDefaultSort).
 */
export function bestimmeAktenSortierung(
  opts: { stellungnahmeStatus?: string | null; substatusStellungnahme?: string[] | null; defaultSort?: AktenSortierung },
): AktenSortierung {
  const stnSubstatus = opts.substatusStellungnahme;
  if (
    istStellungnahmeStatusFilterAktiv(opts.stellungnahmeStatus) ||
    (stnSubstatus && stnSubstatus.length > 0)
  ) {
    return { column: 'schriftsatzfrist', ascending: true, nullsFirst: false };
  }
  return opts.defaultSort ?? { column: 'created_at', ascending: false, nullsFirst: false };
}

/**
 * Gibt das kurze Label für einen Stellungnahme-Unter-Status zurück.
 * `null` / unbekannte Werte ergeben `null`, damit nichts gerendert wird.
 */
export function stellungnahmeStatusLabel(
  status: string | null | undefined,
): string | null {
  const normal = normalisiereStellungnahmeFilter(status);
  if (!normal) return null;
  switch (normal) {
    case 'offen':       return 'Offen';
    case 'versendet':   return 'Versendet';
    case 'abgerechnet': return 'Abgerechnet';
  }
}

/**
 * Tailwind-Klassen für den Stellungnahme-Chip. Pro Unter-Status
 * ist eine Farbe festgelegt (siehe Issue #413):
 *   - offen        → gelb
 *   - versendet    → grün
 *   - abgerechnet  → grau
 *
 * Für `null`/unbekannte Werte `null`, damit nichts gerendert wird.
 */
export function stellungnahmeStatusChipClasses(
  status: string | null | undefined,
): string | null {
  const normal = normalisiereStellungnahmeFilter(status);
  if (!normal) return null;
  switch (normal) {
    case 'offen':       return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'versendet':   return 'bg-green-50 text-green-700 border border-green-200';
    case 'abgerechnet': return 'bg-gray-100 text-gray-500 border border-gray-200';
  }
}

export type FristLevel = 'kritisch' | 'warnung' | 'normal';

export interface FristenBadge {
  level: FristLevel;
  tageBisFrist: number;
}

/**
 * Berechnet das Schriftsatzfrist-Badge (Issue #413).
 *   - `kritisch` (rot)    : Frist heute oder überfällig, oder < 14 Tage
 *   - `warnung`  (gelb)   : 14–29 Tage
 *   - `normal`            : ≥ 30 Tage
 *   - `null`              : kein Datum / ungültig / Stellungnahme inaktiv
 *
 * `tageBisFrist` ist ganzzahlig (kann negativ sein = überfällig).
 * `today` Parameter erlaubt deterministische Tests.
 */
export function berechneSchriftsatzfristBadge(
  schriftsatzfrist: string | null | undefined,
  today: Date = new Date(),
): FristenBadge | null {
  if (!schriftsatzfrist) return null;
  const frist = new Date(schriftsatzfrist);
  if (Number.isNaN(frist.getTime())) return null;

  // `frist` kommt als "YYYY-MM-DD" → als UTC-Mitternacht geparst, daher UTC-Accessoren.
  // `today` ist ein realer Zeitpunkt: lokale Accessoren, damit in CET zwischen
  // 00:00–02:00 Ortszeit der heutige Tag und nicht der gestrige UTC-Tag zählt.
  const fristMidnight = Date.UTC(frist.getUTCFullYear(), frist.getUTCMonth(), frist.getUTCDate());
  const heuteMidnight = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const tageBisFrist = Math.round((fristMidnight - heuteMidnight) / (1000 * 60 * 60 * 24));

  let level: FristLevel;
  if (tageBisFrist < 14) level = 'kritisch';
  else if (tageBisFrist < 30) level = 'warnung';
  else level = 'normal';

  return { level, tageBisFrist };
}

/**
 * Tailwind-Klassen für das Schriftsatzfrist-Badge.
 */
export function schriftsatzfristBadgeClasses(level: FristLevel): string {
  switch (level) {
    case 'kritisch': return 'bg-red-50 text-red-700 border border-red-200';
    case 'warnung':  return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'normal':   return 'bg-gray-50 text-gray-500 border border-gray-200';
  }
}

/**
 * Menschlich lesbarer Badge-Text, z. B. "Frist 7 Tage", "Frist heute",
 * "Frist überfällig (3 Tage)".
 */
export function schriftsatzfristBadgeText(badge: FristenBadge): string {
  if (badge.tageBisFrist < 0) return `Frist überfällig (${Math.abs(badge.tageBisFrist)} Tag${Math.abs(badge.tageBisFrist) === 1 ? '' : 'e'})`;
  if (badge.tageBisFrist === 0) return 'Frist heute';
  if (badge.tageBisFrist === 1) return 'Frist 1 Tag';
  return `Frist ${badge.tageBisFrist} Tage`;
}
